import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery as useReactQuery } from '@tanstack/react-query';
import type { CellClassParams, GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { ChuteSchedulerActions } from '@/app/slices/chuteScheduler';
import { addToast } from '@/app/slices/toast';
import {
  GridLoading,
  SiteSelect,
  type WorkflowTab,
  WorkflowTabBar,
} from '@/components/common';
import { parseExistingComments } from '@/components/common/revisions-button/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ColDef, ColGroupDef } from '@/grid/types';
import { exportGridToExcel } from '@/hooks/useExcelExport';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { getValidSites } from '@/services/auth';
import {
  allTopRevisionsOptions,
  chuteScheduleCombinedOptions,
  chuteScheduleCombinedV2Options,
  chuteScheduleCombinedV3Options,
  replaceRevision as replaceRevisionRest,
  updateRevisionsBatch as updateRevisionsBatchRest,
  updateRevisions as updateRevisionsRest,
} from '@/services/chute-scheduler/api';
import { useContextMenu } from '@/utils/context-menu';

import RevisionsButton from '../../components/common/revisions-button/RevisionsButton';
import { ApplyButton } from './components/ApplyButton';
import { ChuteSchedulerCalendar as Calendar } from './components/Calendar';
import { Grid } from './components/Grid';
import { Interval } from './components/Interval';
import RevisionActions from './components/RevisionActions';
import { SaveButton } from './components/SaveButton';
import { StartTime } from './components/StartTime';
import {
  getResultsPartnershipColumnDef,
  schedulingAddedColumnDefs,
  schedulingColumnDefs,
  useResultsColumnDefs,
} from './utils/columnDefs';
import {
  COLOR_CLASSES,
  COLUMNS_TO_SHADE,
  EXPORT_COLOR_RULES,
} from './utils/constants';

export type ChuteSchedulerProps = { className?: string };

type ChuteTab = 'scheduling' | 'results';

const CHUTE_TABS: WorkflowTab<ChuteTab>[] = [
  { key: 'scheduling', label: 'Scheduling', icon: 'pi pi-calendar-clock' },
  { key: 'results', label: 'Results', icon: 'pi pi-check-square' },
];

export const ChuteSchedulerPage = ({ className }: ChuteSchedulerProps) => {
  const [params, setParams] = useSearchParams();
  const site = params.get('site');
  const date = params.get('date') as string;
  const isMultiSelectEnabled = useFeatureFlag(
    'Select_Multiple_Chute_Schedules',
  );
  const isAddColumnsEnabled = useFeatureFlag('Chute_Scheduler_Add_Columns');
  const isPartnershipColumnEnabled = useFeatureFlag(
    'Chute_Scheduler_Partnership_Column',
  );
  const isBatchPoolRevisionEnabled = useFeatureFlag(
    'Batch_Pool_Revision_Comment',
  );

  // Active tab is persisted in the URL (?tab=) so it survives reloads/sharing.
  const activeTab: ChuteTab =
    params.get('tab') === 'results' ? 'results' : 'scheduling';
  const activeIndex = activeTab === 'results' ? 1 : 0;
  const setActiveTab = useCallback(
    (tab: ChuteTab) => {
      const next = new URLSearchParams(params);
      next.set('tab', tab);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const dispatch = useDispatch();
  const gridRef = useRef<AgGridReact>(null);

  const [showAllTopRevisionNumbersDialog, setShowAllTopRevisionNumbersDialog] =
    useState(false);
  const [showAddRevisionCommentsDialog, setShowAddRevisionCommentsDialog] =
    useState(false);
  const [revisionComments, setRevisionComments] = useState<string>('');

  const { t } = useTranslation();

  const me = useAppSelector((state) => state.authentication.me);

  const handleSetValue = async (fieldName: string, value: string) => {
    if (fieldName === 'revisionComments') {
      setRevisionComments(value);

      const selectedNodes = gridRef.current?.api?.getSelectedNodes();
      if (!selectedNodes || selectedNodes.length === 0) {
        console.warn('No rows selected in the grid');
        return;
      }

      const newComments = parseExistingComments(value);

      const updates: Array<{
        donorScheduleId: string;
        revisionComment: string;
      }> = [];

      selectedNodes.forEach((node) => {
        const rowData = node.data;
        if (rowData?.donorScheduleId) {
          const existingComments = parseExistingComments(
            rowData?.revisionComment || '',
          );

          const commentsToAdd = newComments.filter(
            (newComment) => !existingComments.includes(newComment),
          );

          commentsToAdd.forEach((comment) => {
            updates.push({
              donorScheduleId: rowData.donorScheduleId,
              revisionComment: comment,
            });
          });
        }
      });

      if (updates.length > 0) {
        await updateRevisionsRest(updates);
        refetch?.();
        refetchAllTopRevisions?.();
      }
    }
  };

  const handleSetValueV2 = async (fieldName: string, value: string) => {
    if (fieldName !== 'revisionComments') return;

    setRevisionComments(value);

    const selectedNodes = gridRef.current?.api?.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length === 0) {
      console.warn('No rows selected in the grid');
      return;
    }

    const newComments = parseExistingComments(value);

    if (selectedNodes.length === 1) {
      const rowData = selectedNodes[0].data;
      if (!rowData?.donorScheduleId) return;

      const existingComments = parseExistingComments(
        rowData.revisionComment || '',
      );
      const hasDeleted = existingComments.some((c) => !newComments.includes(c));
      const commentsToAdd = newComments.filter(
        (c) => !existingComments.includes(c),
      );

      if (!hasDeleted && commentsToAdd.length === 0) return;

      if (hasDeleted) {
        await replaceRevisionRest(rowData.donorScheduleId, value);
      } else {
        await updateRevisionsRest(
          commentsToAdd.map((comment) => ({
            donorScheduleId: rowData.donorScheduleId,
            revisionComment: comment,
          })),
        );
      }
    } else {
      const allHaveSplitOrPool = selectedNodes.every(
        (node) =>
          node.data?.revisionComment?.includes('Split') ||
          node.data?.revisionComment?.includes('Pool'),
      );
      if (!allHaveSplitOrPool) return;

      for (const comment of newComments) {
        const donorScheduleIds = selectedNodes
          .filter((node) => {
            const existing = parseExistingComments(
              node.data?.revisionComment || '',
            );
            return !existing.includes(comment);
          })
          .map((node) => node.data.donorScheduleId as string)
          .filter(Boolean);

        if (donorScheduleIds.length > 0) {
          await updateRevisionsBatchRest({
            donorScheduleIds,
            revisionComment: comment,
          });
        }
      }
    }

    refetch?.();
    refetchAllTopRevisions?.();
  };

  const handleGetValue = (fieldName: string) => {
    if (fieldName === 'revisionComments') {
      const selectedNodes = gridRef.current?.api?.getSelectedNodes();
      if (selectedNodes && selectedNodes.length === 1) {
        const rowData = selectedNodes[0].data;
        return rowData?.revisionComment || '';
      }
      if (isBatchPoolRevisionEnabled) {
        if (!selectedNodes || selectedNodes.length === 0) {
          return '';
        }
        const firstComment = selectedNodes[0].data?.revisionComment || '';
        const allMatch = selectedNodes.every(
          (node) => (node.data?.revisionComment || '') === firstComment,
        );
        return allMatch ? firstComment : '';
      }
      return revisionComments;
    }
    return null;
  };

  const {
    schedulingRows,
    resultsRows,
    selectedResultsRows,
    multiChuteCount,
    error,
    loading,
  } = useAppSelector((s) => s.chuteScheduler);

  const hasSelectedRows = isMultiSelectEnabled
    ? selectedResultsRows.length === 1 ||
      (selectedResultsRows.length > 1 &&
        selectedResultsRows.every(
          (r: any) =>
            r.revisionComment?.includes('Split') ||
            r.revisionComment?.includes('Pool'),
        ))
    : selectedResultsRows.length === 1;

  const {
    data: restData,
    refetch: refetchRest,
    isLoading: restLoading,
    isError: restError,
  } = useReactQuery({
    ...(isPartnershipColumnEnabled
      ? chuteScheduleCombinedV3Options({ siteId: site!, date: date! })
      : isAddColumnsEnabled
        ? chuteScheduleCombinedV2Options({ siteId: site!, date: date! })
        : chuteScheduleCombinedOptions({ siteId: site!, date: date! })),
    enabled: !!site && !!date,
  });

  const { data: restTopRevisions, refetch: refetchRestTopRevisions } =
    useReactQuery({
      ...allTopRevisionsOptions({ date: date! }),
      enabled: !!date,
      refetchInterval: date ? 20000 : false,
    });

  useEffect(() => {
    dispatch({
      type: ChuteSchedulerActions.SET_LOADING,
      loading: restLoading,
    });
  }, [restLoading, dispatch]);

  useEffect(() => {
    if (restError) {
      dispatch({
        type: ChuteSchedulerActions.SET_ERROR,
        error: 'Unable to load schedule. Try another date and/or site.',
      });
    }
  }, [restError, dispatch]);

  useEffect(() => {
    if (!restData) return;

    if (!restData.scheduling.length && !restData.results.length) {
      dispatch({
        type: ChuteSchedulerActions.SET_ERROR,
        error: 'No records found. Try another date and/or site.',
      });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
        rows: [],
      });
      dispatch({ type: ChuteSchedulerActions.UPDATE_RESULTS_ROWS, rows: [] });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
        rows: [],
      });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SELECTED_RESULTS_ROWS,
        rows: [],
      });
      return;
    }

    dispatch({
      type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
      rows: restData.scheduling,
    });
    dispatch({
      type: ChuteSchedulerActions.UPDATE_RESULTS_ROWS,
      rows: restData.results,
    });
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
      rows: [],
    });
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_RESULTS_ROWS,
      rows: [],
    });
  }, [restData, dispatch]);

  const refetch = refetchRest;
  const allTopRevisionNumbers = { allTopRevisions: restTopRevisions };
  const refetchAllTopRevisions = refetchRestTopRevisions;

  useEffect(() => {
    if (error) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'No data returned',
          detail: error,
          life: 6000,
        }),
      );
      dispatch({ type: ChuteSchedulerActions.SET_ERROR, error: null });
    }
  }, [error, dispatch]);

  const [isShaded, setIsShaded] = useState(false);

  const rows = useMemo(
    () => (activeIndex === 0 ? schedulingRows : resultsRows),
    [activeIndex, schedulingRows, resultsRows],
  );

  const exportAutoFilter = useFeatureFlag('Export_To_Excel_Filter_Dropdown');
  const exportKeepColor = useFeatureFlag('Excel_Keep_Color');

  const resultsColumnDefs = useResultsColumnDefs();
  const columnDefs = useMemo(() => {
    const base = activeIndex === 0 ? schedulingColumnDefs : resultsColumnDefs;
    const withAdded =
      activeIndex === 0 && isAddColumnsEnabled
        ? [...base, ...schedulingAddedColumnDefs]
        : base;
    const withPartnership =
      activeIndex === 1 && isPartnershipColumnEnabled
        ? withAdded.flatMap((col) =>
            col.field === 'account'
              ? [col, getResultsPartnershipColumnDef(t)]
              : [col],
          )
        : withAdded;
    return withPartnership.map((col) =>
      col.field === 'embryoPlan' ? { ...col, hide: true } : col,
    );
  }, [
    activeIndex,
    isAddColumnsEnabled,
    isPartnershipColumnEnabled,
    resultsColumnDefs,
    t,
  ]);

  const gridApiRef = useRef<GridApi | null>(null);

  const handleExport = () => {
    const api = gridRef?.current?.api;
    if (api) {
      exportGridToExcel(api, {
        sheetName: activeIndex === 0 ? 'Scheduling' : 'Results',
        filename: `chute-schedule-${date}-${activeIndex === 0 ? 'scheduling' : 'results'}.xlsx`,
        autoFilter: exportAutoFilter,
        preserveColors: exportKeepColor,
      });
    }
  };

  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  const handleRevert = () => setShowRevertConfirm(true);

  const confirmRevert = async () => {
    setShowRevertConfirm(false);
    const res = await refetch();
    if (res.data) {
      dispatch({
        type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
        rows: res.data.scheduling,
      });
      dispatch({
        type: ChuteSchedulerActions.UPDATE_RESULTS_ROWS,
        rows: res.data.results,
      });
    }
  };

  const generateColorCombinations = useCallback((api: GridApi<any>) => {
    const combinationColors = new Map<string, string>();
    const colorValues = Object.values(COLOR_CLASSES);

    const allCombinations = new Set<string>();
    api.forEachNode((node) => {
      const sire = node?.data?.sire || '';
      const semenType = node?.data?.semenType || '';
      if (sire) allCombinations.add(`${sire}-${semenType}`);
    });

    Array.from(allCombinations).forEach((combo, index) => {
      combinationColors.set(combo, colorValues[index % colorValues.length]);
    });

    return { combinationColors, colorValues };
  }, []);

  const assignNodeColors = useCallback(
    (api: GridApi<any>, combinationColors: Map<string, string>) => {
      const nodeColors = new Map<string, string>();
      api.forEachNode((node) => {
        const sire = node?.data?.sire || '';
        const semenType = node?.data?.semenType || '';
        const color = combinationColors.get(`${sire}-${semenType}`);
        if (color) nodeColors.set(node.id as string, color);
      });
      return nodeColors;
    },
    [],
  );

  const updateColumnDefinitions = useCallback(
    (
      api: GridApi<any>,
      nodeColors: Map<string, string>,
      colorValues: string[],
      isClearing: boolean,
    ): (ColDef<any, any> | ColGroupDef<any>)[] => {
      if (isClearing) {
        const uncoloredColumns = api.getColumns()?.map((col) => ({
          ...col.getColDef(),
          cellClassRules: {},
        }));

        return uncoloredColumns as (ColDef<any, any> | ColGroupDef<any>)[];
      }

      const updatedColumns = api.getColumns()?.map((col) => {
        const colDef = col.getColDef();
        const field = colDef.field;

        if (COLUMNS_TO_SHADE.includes(field!)) {
          return {
            ...colDef,
            cellClassRules: Object.fromEntries(
              colorValues.map((color) => [
                color,
                (params: CellClassParams) =>
                  nodeColors.get(params.node.id!) === color,
              ]),
            ),
          };
        }

        if (field === 'export') {
          return {
            ...colDef,
            cellClassRules: {
              ...Object.entries(EXPORT_COLOR_RULES).reduce(
                (rules, [key, color]) => ({
                  ...rules,
                  [color]: (params: CellClassParams) =>
                    key === 'default'
                      ? !params.value || params.value === ''
                      : params.value?.includes(key),
                }),
                {},
              ),
            },
          };
        }
        if (field === 'pooled') {
          return {
            ...colDef,
            cellClassRules: {
              'bg-tog-light-blue-shaded': (params: CellClassParams) =>
                params.value !== 'No',
            },
          };
        }

        if (field === 'inMat') {
          return {
            ...colDef,
            cellClassRules: {
              'tog-pink-background': (params: CellClassParams) =>
                params.node.data.inMat >= 40,
            },
          };
        }

        return {
          ...colDef,
          cellClassRules: {},
        };
      });

      return updatedColumns as (ColDef<any, any> | ColGroupDef<any>)[];
    },
    [],
  );

  const handleShadeRows = useCallback(() => {
    if (!gridRef.current?.api) return;
    const api = gridRef.current.api;

    if (!isShaded) {
      const { combinationColors, colorValues } = generateColorCombinations(api);
      const nodeColors = assignNodeColors(api, combinationColors);
      const updatedColumns = updateColumnDefinitions(
        api,
        nodeColors,
        colorValues,
        false,
      );

      if (updatedColumns) {
        api.setGridOption('columnDefs', updatedColumns);
      }
    } else {
      const uncoloredColumns = updateColumnDefinitions(
        api,
        new Map(),
        [],
        true,
      );
      if (uncoloredColumns) {
        api.setGridOption('columnDefs', uncoloredColumns);
      }
    }

    api.refreshCells();
    setIsShaded((prev) => !prev);
  }, [
    assignNodeColors,
    generateColorCombinations,
    isShaded,
    updateColumnDefinitions,
  ]);

  const { getContextMenuItems: schedulingContextMenu } = useContextMenu({
    idKey: 'aspirationId',
  });
  const { getContextMenuItems: resultsContextMenu } = useContextMenu({
    idKey: 'donorScheduleId',
  });
  const getContextMenuItems =
    activeIndex === 0 ? schedulingContextMenu : resultsContextMenu;

  const validSites = getValidSites(me);

  return (
    <TooltipProvider>
      <div className={`${className ?? ''} flex h-[calc(100vh-80px)] flex-col`}>
        {/* Filters + actions — always visible (replaces the old accordion). */}
        <div className="mb-2 flex w-full flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex w-full flex-col gap-3">
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-end">
              <Calendar className="w-full sm:w-auto" />
              <SiteSelect
                paramKey="site"
                label={t('site.site')}
                validSites={validSites}
                className="w-full sm:w-auto"
                triggerClassName="w-full sm:w-56"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={handleRevert}
              >
                <i className="pi pi-refresh text-xs" />
                Refresh
              </Button>
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={handleExport}
              >
                <i className="pi pi-file-excel text-xs" />
                Export
              </Button>
              <Button
                variant="outline"
                onClick={handleShadeRows}
                disabled={activeIndex !== 0}
              >
                {isShaded ? 'Unshade Rows' : 'Shade Rows'}
              </Button>
            </div>
          </div>

          {/* Both action blocks share one grid cell so the area always
                reserves the taller (Scheduling) height — switching tabs never
                shifts the tabs/table below. */}
          <div className="grid w-full md:w-auto">
            <div
              aria-hidden={activeIndex !== 0}
              className={`col-start-1 row-start-1 w-full flex-col gap-3 md:max-w-none ${
                activeIndex === 0 ? 'flex' : 'hidden md:invisible md:flex'
              }`}
            >
              <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-nowrap lg:justify-end">
                <StartTime className="w-full lg:w-auto" />
                <Interval className="w-full lg:w-auto" />

                <div className="label-input-group flex w-full flex-col gap-1 lg:w-fit">
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <label className="w-fit cursor-help text-sm">
                          Chutes
                        </label>
                      }
                    />
                    <TooltipContent>
                      Number of parallel chutes to use when scheduling. Increase
                      this to split donors across multiple concurrent chutes.
                    </TooltipContent>
                  </Tooltip>

                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={multiChuteCount}
                    onChange={(e) =>
                      dispatch({
                        type: ChuteSchedulerActions.UPDATE_MULTI_CHUTE_COUNT,
                        count: Math.min(
                          20,
                          Math.max(1, Number(e.target.value) || 1),
                        ),
                      })
                    }
                    className="w-full lg:w-20"
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <ApplyButton />
                <SaveButton />
              </div>
            </div>

            <div
              aria-hidden={activeIndex !== 1}
              className={`col-start-1 row-start-1 w-full flex-wrap content-start items-start justify-start gap-2 sm:justify-end ${
                activeIndex === 1 ? 'flex' : 'hidden md:invisible md:flex'
              }`}
            >
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => setShowAllTopRevisionNumbersDialog(true)}
              >
                <i className="pi pi-eye text-xs" />
                All Revision Numbers
              </Button>
              <RevisionsButton
                showAddRevisionCommentsDialog={showAddRevisionCommentsDialog}
                setShowAddRevisionCommentsDialog={
                  setShowAddRevisionCommentsDialog
                }
                handleGetValue={handleGetValue}
                handleSetValue={
                  isMultiSelectEnabled ? handleSetValueV2 : handleSetValue
                }
                disabled={!hasSelectedRows}
                isMultiSelectEnabled={isMultiSelectEnabled}
                selectedRowCount={selectedResultsRows.length}
                isBatchPoolRevisionEnabled={isBatchPoolRevisionEnabled}
              />
              <RevisionActions
                resultsRows={resultsRows}
                gridRef={gridRef}
                label="Revision Actions"
                icon="pi pi-cog"
                severity="secondary"
                disabled={rows.length < 1 || activeIndex !== 1}
                refetch={refetch}
              />
            </div>
          </div>
        </div>

        <WorkflowTabBar<ChuteTab>
          tabs={CHUTE_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <div className="rounded-b-lg bg-[var(--surface-0)] p-4">
          <div className="h-[70vh] overflow-x-auto md:h-[60vh]">
            {loading ? (
              <GridLoading label="Loading schedules…" />
            ) : (
              <Grid
                refreshData={refetch}
                columnDefs={columnDefs}
                rowData={rows}
                gridKey={activeIndex}
                setGridApi={(api: GridApi | null) => (gridApiRef.current = api)}
                gridRef={gridRef}
                date={date}
                getContextMenuItems={getContextMenuItems}
              />
            )}
          </div>
        </div>

        <Dialog
          open={showAllTopRevisionNumbersDialog}
          onOpenChange={setShowAllTopRevisionNumbersDialog}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                All Top Revision Numbers Per Fertilization Site
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Revision Number</TableHead>
                    <TableHead>Fertilization Site Name</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(allTopRevisionNumbers?.allTopRevisions ?? []).map(
                    (
                      r: {
                        revisionNumber?: string | number | null;
                        fertilizationSiteName?: string | null;
                      },
                      i: number,
                    ) => (
                      <TableRow key={i}>
                        <TableCell>{r.revisionNumber}</TableCell>
                        <TableCell>{r.fertilizationSiteName}</TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showRevertConfirm} onOpenChange={setShowRevertConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Revert changes?</DialogTitle>
              <DialogDescription>
                This will discard all unsaved changes and reload the schedule
                from the database. Continue?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowRevertConfirm(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmRevert}>
                Yes, Revert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};
